import type { HeadingTypes } from '@etg/wings';
import {
  breakpoints,
  Button,
  Card,
  Heading,
  Inline,
  Stack,
  Text,
  textVariants,
  vars,
} from '@etg/wings';
import { useDateFormat, useProperty, useTranslation } from '@eti/providers';
import { css, cx, etiColors } from '@eti/styles';
import { Phone } from '@phosphor-icons/react';
import { Accordion, Notification, variants } from '@eti/components';
import { getLocalSchedule } from '../utils';
import type { ScheduleType } from '../models';
import ContactSchedule from './ContactSchedule';

const cardStyles = css`
  background-color: #fafafa;
  border: 1px solid ${vars.colors.divider};
`;

const notificationStyles = (isHeaderVisible: boolean) => css`
  margin: ${isHeaderVisible ? '0 0 8px' : '16px 0 0'};

  a {
    color: ${etiColors.info.solid};
    text-decoration: none;
  }
`;

const operatorClarificationTextStyles = css`
  font-size: 0.75rem;

  &:empty {
    display: none;
  }
`;

interface CallProps {
  className?: string;
  shouldShowHeader?: boolean;
  headingAs?: `h${HeadingTypes['level']}` | 'h5';
}

interface CallHeaderProps {
  className?: string;
  headingAs?: `h${HeadingTypes['level']}` | 'h5';
}

export const CallHeader = ({ className, headingAs = 'h3' }: CallHeaderProps) => {
  const { t } = useTranslation();

  return (
    <Inline alignY="center" className={className} spacing={8}>
      <Phone aria-hidden="true" color={etiColors.black} data-testid="call-icon" size={24} />
      <Heading as={headingAs} data-testid="call-title" level={3}>
        {t('ContactUs.ContactSection.Call.Title')}
      </Heading>
    </Inline>
  );
};

const Call = ({ className, shouldShowHeader = true, headingAs }: CallProps) => {
  const { p } = useProperty();
  const { t } = useTranslation();
  const { time } = useDateFormat();

  const isSecondaryPhoneEnabled = p('ContactUs.Call.SecondaryPhoneNumber.Enabled');

  const phoneNumber = isSecondaryPhoneEnabled
    ? t('ContactUs.ContactSection.Call.Secondary.Button')
    : t('ContactUs.ContactSection.Call.Primary.Button');

  const schedule: ScheduleType[] = getLocalSchedule(p('ContactUs.Call.Schedule'), t, time);

  return (
    <>
      {isSecondaryPhoneEnabled && (
        <Notification
          className={notificationStyles(shouldShowHeader)}
          dataTestId="contactUs-call-notification"
          variant={variants.INFORMATION}
        >
          <Text asHtml>{t('ContactUs.ContactSection.Call.Notification')}</Text>
        </Notification>
      )}
      <Card className={cx(cardStyles, className)} data-testid="call-section">
        <Stack spacing={16}>
          {shouldShowHeader && <CallHeader headingAs={headingAs} />}
          <Text data-testid="call-schedule-description">
            {t('ContactUs.ContactSection.Schedule.Description')}
          </Text>
          <Stack spacing={8}>
            <>
              <Text data-testid="call-english-schedule-title" variant={textVariants._14_BOLD}>
                {t('ContactUs.ContactSection.EnglishSchedule.Title')}
              </Text>
              <Text data-testid="call-english-schedule-description">
                {t('ContactUs.ContactSection.CustomerService.Description')}
              </Text>
            </>
            {schedule?.length > 0 && <ContactSchedule isChatSchedule={false} schedule={schedule} />}
          </Stack>
          <Accordion id="show-number-accordion" shouldExpandOnTop spacing={16}>
            <Accordion.Header
              data-testid="show-number-button"
              expandedHeader={<Text>{t('Call.HideNumber')}</Text>}
            >
              <Text>{t('Call.ShowNumber')}</Text>
            </Accordion.Header>
            <Accordion.Body>
              <Stack spacing={8}>
                <Inline align="start" collapseBelow={breakpoints._768}>
                  <Button
                    as="a"
                    data-testid="call-button"
                    dir="ltr"
                    href={`tel:${phoneNumber.replace(/\s+/g, '')}`}
                    variant="secondary"
                  >
                    {phoneNumber}
                  </Button>
                </Inline>
                <Text
                  className={operatorClarificationTextStyles}
                  data-testid="contact-us-operator-clarification-text"
                >
                  {t('ContactUs.Operator.Clarification.Text')}
                </Text>
              </Stack>
            </Accordion.Body>
          </Accordion>
        </Stack>
      </Card>
    </>
  );
};

export default Call;
